const RELOAD_GUARD_KEY = 'chunk-load-reloaded-at'
const RELOAD_GUARD_MS = 60_000

// Each browser words a failed dynamic import differently, and Vite throws its
// own error when a lazy chunk's CSS fails to load
const CHUNK_LOAD_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i
]

type Deps = {
  storage: Pick<Storage, 'getItem' | 'setItem'>
  reload: () => void
  now: () => number
}

const browserDeps = (): Deps => ({
  storage: window.sessionStorage,
  reload: () => window.location.reload(),
  now: () => Date.now()
})

export function isChunkLoadError(error: unknown): boolean {
  return (
    error instanceof Error && CHUNK_LOAD_ERROR_PATTERNS.some(pattern => pattern.test(error.message))
  )
}

/**
 * Reload the page, at most once per RELOAD_GUARD_MS. Returns whether a reload
 * was started.
 *
 * A browser keeps a failed module fetch for the life of the page, and so does
 * React.lazy, so the only way to retry a chunk that failed to load is to reload.
 * The guard keeps a chunk that stays unreachable from causing a reload loop.
 */
export function reloadOnceForChunkLoadError(deps: Deps = browserDeps()): boolean {
  try {
    const lastReloadAt = Number(deps.storage.getItem(RELOAD_GUARD_KEY))
    if (deps.now() - lastReloadAt < RELOAD_GUARD_MS) return false

    deps.storage.setItem(RELOAD_GUARD_KEY, String(deps.now()))
  } catch {
    // Without storage we cannot guard against a reload loop
    return false
  }

  deps.reload()
  return true
}
